<template>
  <div class="records-content">
    <div class="main-container">
      <h2 class="page-title">회원 기록</h2>

      <!-- 로딩 상태 -->
      <div v-if="loading && currentWeek === 0" class="loading-state">
        <p>운동 기록을 불러오는 중...</p>
      </div>

      <!-- 운동 기록이 없는 경우 -->
      <div
        v-else-if="!recentClass && classes.length === 0 && !loading"
        class="empty-state-wrapper"
      >
        <!-- 주차 선택 컨트롤 -->
        <div class="week-selector">
          <button
            class="week-nav-btn week-nav-left"
            @click="goToPreviousWeek"
            :disabled="currentWeek >= maxWeeks - 1"
          >
            <svg width="16" height="8" viewBox="0 0 12 6">
              <path
                d="M1 1 L6 5 L11 1"
                stroke="#8E8E8E"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                transform="rotate(90 6 3)"
              />
            </svg>
          </button>

          <button
            class="week-selector-main"
            @click="showWeekPicker = !showWeekPicker"
          >
            <span class="week-text">{{ getWeekRangeText(currentWeek) }}</span>
            <span v-if="currentWeek === 0" class="current-badge">현재</span>
            <svg width="8" height="4" viewBox="0 0 8 4" class="dropdown-icon">
              <path
                d="M1 0 L4 3 L7 0"
                stroke="#8E8E8E"
                stroke-width="1"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <button
            class="week-nav-btn week-nav-right"
            @click="goToNextWeek"
            :disabled="currentWeek === 0"
          >
            <svg width="16" height="8" viewBox="0 0 12 6">
              <path
                d="M1 1 L6 5 L11 1"
                stroke="#8E8E8E"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                transform="rotate(-90 6 3)"
              />
            </svg>
          </button>

          <!-- 주차 선택 드롭다운 -->
          <div v-if="showWeekPicker" class="week-picker-dropdown">
            <button
              v-for="week in maxWeeks"
              :key="week - 1"
              :class="[
                'week-picker-item',
                { active: currentWeek === week - 1 },
              ]"
              @click="selectWeek(week - 1)"
            >
              <span>{{ getWeekRangeText(week - 1) }}</span>
              <span v-if="week - 1 === 0" class="current-badge-small"
                >현재</span
              >
            </button>
          </div>
        </div>

        <div class="empty-state">
          <p>이 주차에는 운동 기록이 없습니다.</p>
          <p class="hint">다른 주차를 선택하거나 새로운 수업을 예약해보세요!</p>
        </div>
      </div>

      <!-- 운동 기록이 있는 경우 -->
      <template v-else>
        <!-- 주차 선택 컨트롤 -->
        <div class="week-selector">
          <button
            class="week-nav-btn week-nav-left"
            @click="goToPreviousWeek"
            :disabled="currentWeek >= maxWeeks - 1"
          >
            <svg width="16" height="8" viewBox="0 0 12 6">
              <path
                d="M1 1 L6 5 L11 1"
                stroke="#8E8E8E"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                transform="rotate(90 6 3)"
              />
            </svg>
          </button>

          <button
            class="week-selector-main"
            @click="showWeekPicker = !showWeekPicker"
          >
            <span class="week-text">{{ getWeekRangeText(currentWeek) }}</span>
            <span v-if="currentWeek === 0" class="current-badge">현재</span>
            <svg width="8" height="4" viewBox="0 0 8 4" class="dropdown-icon">
              <path
                d="M1 0 L4 3 L7 0"
                stroke="#8E8E8E"
                stroke-width="1"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <button
            class="week-nav-btn week-nav-right"
            @click="goToNextWeek"
            :disabled="currentWeek === 0"
          >
            <svg width="16" height="8" viewBox="0 0 12 6">
              <path
                d="M1 1 L6 5 L11 1"
                stroke="#8E8E8E"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                transform="rotate(-90 6 3)"
              />
            </svg>
          </button>

          <!-- 주차 선택 드롭다운 -->
          <div v-if="showWeekPicker" class="week-picker-dropdown">
            <button
              v-for="week in maxWeeks"
              :key="week - 1"
              :class="[
                'week-picker-item',
                { active: currentWeek === week - 1 },
              ]"
              @click="selectWeek(week - 1)"
            >
              <span>{{ getWeekRangeText(week - 1) }}</span>
              <span v-if="week - 1 === 0" class="current-badge-small"
                >현재</span
              >
            </button>
          </div>
        </div>

        <!-- 최근 수업 카드 -->
        <div v-if="recentClass" class="recent-card">
          <div class="recent-header">
            <div class="recent-icon" :class="typeClass(recentClass.type)">
              <img :src="getIcon(recentClass.type)" alt="" class="icon" />
            </div>
            <div class="recent-info">
              <h3>
                {{ recentClass.date }}
              </h3>
              <div class="recent-meta">
                <span class="time">{{ formatTime(recentClass.time) }}</span>
                <span class="type" :class="typeClass(recentClass.type)">{{
                  typeLabelOf(recentClass)
                }}</span>
                <span v-if="recentClass.guest_name" class="guest-tag"
                  >게스트: {{ recentClass.guest_name }}</span
                >
                <button
                  v-if="recentClass.guest_name"
                  class="transfer-btn"
                  @click.stop="openTransferModal(recentClass)"
                >
                  이관
                </button>
                <!-- R5 회원이 고른 체감 강도(RPE, A3.2). 공유 이미지에는 넣지 않는다 -->
                <span
                  v-if="recentRpe"
                  class="rpe-tag"
                  :class="`rpe-tag--${recentRpe.level}`"
                  title="회원이 고른 체감 강도(RPE)"
                  data-html2canvas-ignore="true"
                  >{{ recentRpe.text }}</span
                >
              </div>
            </div>
            <button
              class="expand-btn-main"
              @click="recentExpanded = !recentExpanded"
            >
              <svg width="34" height="34" viewBox="0 0 34 34">
                <rect width="34" height="34" rx="5" fill="rgba(51,51,51,0.3)" />
                <path
                  :d="
                    recentExpanded
                      ? 'M12 19 L17 14 L22 19'
                      : 'M12 15 L17 20 L22 15'
                  "
                  stroke="#8E8E8E"
                  stroke-width="2"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <div v-if="recentExpanded" class="recent-expanded">
            <!-- 심박수 그래프 -->
            <div class="hr-chart-container">
              <div class="chart-header">
                <span class="chart-title">심박수</span>
                <button
                  class="share-button-icon"
                  @click="shareWorkout('recent')"
                  :disabled="sharing"
                  title="공유하기"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.18871 15.022 5.37189 15.0627 5.54826L8.91664 9.71529C8.31708 9.25696 7.55251 9 6.75 9C4.67893 9 3 10.6789 3 12.75C3 14.8211 4.67893 16.5 6.75 16.5C7.55251 16.5 8.31708 16.243 8.91664 15.7847L15.0627 19.9517C15.022 20.1281 15 20.3113 15 20.5C15 22.1569 16.3431 23.5 18 23.5C19.6569 23.5 21 22.1569 21 20.5C21 18.8431 19.6569 17.5 18 17.5C16.3431 17.5 15 18.8431 15 20.5C15 20.3113 15.022 20.1281 15.0627 19.9517L8.91664 15.7847C8.31708 16.243 7.55251 16.5 6.75 16.5C4.67893 16.5 3 14.8211 3 12.75C3 10.6789 4.67893 9 6.75 9C7.55251 9 8.31708 9.25696 8.91664 9.71529L15.0627 5.54826C15.022 5.37189 15 5.18871 15 5C15 3.34315 16.3431 2 18 2C19.6569 2 21 3.34315 21 5C21 6.65685 19.6569 8 18 8Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </div>
              <div v-if="recentClass.stats" class="chart-max-hr-label">
                <span class="max-hr-arrow">▲</span>
                {{ recentClass.stats.maxHr }}
              </div>
              <HrLineChart
                :data="recentClass.hr_trace"
                :yMin="60"
                :yMax="200"
                :xMax="recentClass.duration"
                :zoneBoundaries="recentClass.zone_boundaries"
              />
              <div class="chart-footer">
                <div class="chart-min-hr-label">
                  <span class="min-hr-arrow">▼</span>
                  {{ getMinHr(recentClass.hr_trace) }}
                </div>
                <div class="chart-duration-label">
                  {{ formatDuration(recentClass.duration) }}
                </div>
              </div>
            </div>

            <!-- 통계 그리드 -->
            <div class="stats-grid">
              <div class="stat-column-left">
                <div class="stat-card">
                  <div class="stat-label">소모 칼로리</div>
                  <div class="stat-value orange">
                    {{ recentClass.stats.calories
                    }}<span class="unit">kcal</span>
                  </div>
                  <button
                    class="recalc-btn"
                    :disabled="recalcLoading"
                    @click="recalcCalories(recentClass.id, 'recent')"
                  >
                    {{ recalcLoading ? "계산 중..." : "칼로리 재계산" }}
                  </button>
                </div>

                <div class="stat-card">
                  <div class="stat-label">Zone 4+</div>
                  <div class="stat-value purple">
                    {{ Math.round(recentClass.stats.zone4plus)
                    }}<span class="unit">분</span>
                  </div>
                </div>
              </div>

              <div class="stat-card stat-card-hr">
                <div class="stat-label">최대 심박수</div>
                <!-- 데스크톱: 도넛 차트 -->
                <DoughnutChart
                  v-if="!isMobile"
                  :maxHr="recentClass.stats.maxHr"
                  :percentage="recentClass.stats.maxHrPercent"
                />
                <!-- 모바일: 심플 텍스트 -->
                <div v-else class="hr-mobile-display">
                  <div class="hr-number">{{ recentClass.stats.maxHr }}</div>
                  <div class="hr-unit">BPM</div>
                </div>
              </div>

              <div class="stat-card stat-card-zones">
                <div class="zone-list">
                  <ZoneBar
                    v-for="z in sortedZones(recentClass.stats.zones)"
                    :key="z.name"
                    :zone="z"
                  />
                </div>
              </div>
            </div>
            <div class="logo-container">
              <svg
                width="126"
                height="16"
                viewBox="0 0 126 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="logo-image"
              >
                <path
                  d="M119.141 3.39808H120.065L122.452 7.24922H122.52L124.907 3.39808H125.83L122.891 8.05999V11.5508H122.08V8.05999L119.141 3.39808Z"
                  fill="white"
                />
                <path
                  d="M113.632 11.5508V3.39808H116.301C118.057 3.39808 118.947 4.4228 118.947 5.8529C118.947 6.94518 118.429 7.7841 117.415 8.11629L119.285 11.5508H118.361L116.593 8.27394L116.312 8.2852H114.431V11.5508H113.632ZM114.431 7.55326H116.278C117.55 7.55326 118.159 6.90014 118.159 5.8529C118.159 4.80566 117.55 4.11876 116.267 4.11876H114.431V7.55326Z"
                  fill="white"
                />
                <path
                  d="M112.39 7.47444C112.39 10.0306 110.915 11.6634 108.854 11.6634C106.782 11.6634 105.307 10.0306 105.307 7.47444C105.307 4.91827 106.782 3.28547 108.854 3.28547C110.915 3.28547 112.39 4.91827 112.39 7.47444ZM111.613 7.47444C111.613 5.30113 110.431 4.03994 108.854 4.03994C107.278 4.03994 106.095 5.30113 106.095 7.47444C106.095 9.63648 107.266 10.9089 108.854 10.9089C110.431 10.9089 111.613 9.64774 111.613 7.47444Z"
                  fill="white"
                />
                <path
                  d="M98.9416 4.11876V3.39808H104.898V4.11876H102.331V11.5508H101.52V4.11876H98.9416Z"
                  fill="white"
                />
                <path
                  d="M97.4408 5.95425C97.2156 4.83944 96.2471 4.03994 95.0085 4.03994C93.477 4.03994 92.2496 5.24483 92.2496 7.47444C92.2496 9.70405 93.477 10.9089 95.0085 10.9089C96.2471 10.9089 97.2156 10.1094 97.4408 8.99463H98.2515C97.9813 10.5148 96.7313 11.6634 95.0085 11.6634C92.9365 11.6634 91.4614 10.0306 91.4614 7.47444C91.4614 4.91827 92.9365 3.28547 95.0085 3.28547C96.7313 3.28547 97.97 4.41154 98.2515 5.95425H97.4408Z"
                  fill="white"
                />
                <path
                  d="M85.4225 11.5508H84.5779L87.5395 3.39808H88.4066L91.3681 11.5508H90.5236L89.6678 9.12975H86.2783L85.4225 11.5508ZM86.5373 8.42033H89.42L88.0012 4.43406H87.9449L86.5373 8.42033Z"
                  fill="white"
                />
                <path
                  d="M80.3183 11.5508V3.39808H85.0815V4.11876H81.1178V7.11409H84.7099V7.82352H81.1178V11.5508H80.3183Z"
                  fill="white"
                />
                <path
                  d="M79.2003 7.47444C79.2003 10.1883 77.4662 11.6634 75.2591 11.6634C73.0295 11.6634 71.3066 10.177 71.3066 7.47444C71.3066 4.76062 73.0295 3.28547 75.2591 3.28547C77.4662 3.28547 79.2003 4.76062 79.2003 7.47444ZM76.9932 7.47444C76.9932 5.97677 76.3739 5.15474 75.2591 5.15474C74.133 5.15474 73.5137 5.97677 73.5137 7.47444C73.5137 8.9721 74.133 9.79413 75.2591 9.79413C76.3739 9.79413 76.9932 8.9721 76.9932 7.47444Z"
                  fill="white"
                />
                <path
                  d="M70.6053 3.39808V11.5508H68.4657V3.39808H70.6053Z"
                  fill="white"
                />
                <path
                  d="M63.7106 11.5508H60.6139V3.39808H63.6881C66.1992 3.39808 67.7644 4.92953 67.7644 7.47444C67.7644 10.0193 66.1992 11.5508 63.7106 11.5508ZM62.7534 9.72657H63.6318C64.893 9.72657 65.6024 9.19732 65.6024 7.47444C65.6024 5.75156 64.893 5.2223 63.5755 5.2223H62.7534V9.72657Z"
                  fill="white"
                />
                <path
                  d="M53.5053 11.5508V3.39808H56.9736C58.7978 3.39808 59.9914 4.43406 59.9914 6.15694C59.9914 7.283 59.4734 8.08251 58.5951 8.49916L60.2617 11.5508H57.9307L56.4894 8.83698H55.6448V11.5508H53.5053ZM55.6448 7.15914H56.4781C57.2889 7.15914 57.7506 6.86636 57.7506 6.15694C57.7506 5.43626 57.2889 5.1097 56.4781 5.1097H55.6448V7.15914Z"
                  fill="white"
                />
                <path
                  d="M47.0273 11.5508H44.7076L47.4214 3.39808H50.3379L53.0405 11.5508H50.732L50.2478 9.95178H47.5115L47.0273 11.5508ZM48.0069 8.33025H49.7523L48.9078 5.59391H48.8402L48.0069 8.33025Z"
                  fill="white"
                />
                <path
                  d="M42.3847 6.4272C42.3172 5.66147 41.7992 5.15474 40.9321 5.15474C39.806 5.15474 39.1642 6.02181 39.1642 7.47444C39.1642 9.00589 39.8173 9.79413 40.9208 9.79413C41.7429 9.79413 42.2721 9.3437 42.3847 8.62302L44.558 8.64554C44.4454 10.0419 43.218 11.6634 40.8758 11.6634C38.6349 11.6634 36.9571 10.177 36.9571 7.47444C36.9571 4.76062 38.68 3.28547 40.8758 3.28547C42.9027 3.28547 44.3778 4.4228 44.558 6.4272H42.3847Z"
                  fill="white"
                />
                <path
                  d="M5.76545 0C2.58124 0 0 2.58128 0 5.76545V9.60909C0 12.7933 2.58124 15.3745 5.76545 15.3745H20.6403V7.57196L24.484 12.8377H30.7491V7.57196H20.6403V5.26578H30.7491V0H21.14C17.9558 0 15.3745 2.58128 15.3745 5.76545V10.1088H5.26578V5.26578H11.5309L15.3745 0H5.76545Z"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- 수업 리스트 -->
        <div class="class-list">
          <div
            v-for="cls in classes"
            :key="cls.id"
            class="recent-card class-item-wrapper"
            :data-workout-id="cls.id"
          >
            <div class="recent-header">
              <div class="recent-icon" :class="typeClass(cls.type)">
                <img :src="getIcon(cls.type)" alt="" class="icon" />
              </div>
              <div class="recent-info">
                <h3>{{ cls.date }}</h3>
                <div class="recent-meta">
                  <span class="time">{{ formatTime(cls.time) }}</span>
                  <span class="type" :class="typeClass(cls.type)">{{
                    typeLabelOf(cls)
                  }}</span>
                  <span v-if="cls.guest_name" class="guest-tag"
                    >게스트: {{ cls.guest_name }}</span
                  >
                  <button
                    v-if="cls.guest_name"
                    class="transfer-btn"
                    @click.stop="openTransferModal(cls)"
                  >
                    이관
                  </button>
                  <!-- R5 RPE: 펼쳐서 상세를 불러오면 상세 값, 아니면 요약 행에 있을 때만 -->
                  <span
                    v-if="rpeOf(classDetails[cls.id] || cls)"
                    class="rpe-tag"
                    :class="`rpe-tag--${
                      rpeOf(classDetails[cls.id] || cls).level
                    }`"
                    title="회원이 고른 체감 강도(RPE)"
                    data-html2canvas-ignore="true"
                    >{{ rpeOf(classDetails[cls.id] || cls).text }}</span
                  >
                </div>
              </div>
              <button
                class="expand-btn-main"
                @click="toggleClassExpanded(cls.id)"
              >
                <svg width="34" height="34" viewBox="0 0 34 34">
                  <rect
                    width="34"
                    height="34"
                    rx="5"
                    fill="rgba(51,51,51,0.3)"
                  />
                  <path
                    :d="
                      expandedClasses[cls.id]
                        ? 'M12 19 L17 14 L22 19'
                        : 'M12 15 L17 20 L22 15'
                    "
                    stroke="#8E8E8E"
                    stroke-width="2"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div v-if="expandedClasses[cls.id]" class="recent-expanded">
              <!-- 상세 정보 로딩 중 -->
              <div v-if="loadingClassDetails[cls.id]" class="loading-state">
                <p>기록을 불러오는 중...</p>
              </div>

              <!-- 심박수 그래프 -->
              <div v-else-if="classDetails[cls.id]" class="hr-chart-container">
                <div class="chart-header">
                  <span class="chart-title">심박수</span>
                  <button
                    class="share-button-icon"
                    @click="shareWorkout(cls.id)"
                    :disabled="sharing"
                    title="공유하기"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.18871 15.022 5.37189 15.0627 5.54826L8.91664 9.71529C8.31708 9.25696 7.55251 9 6.75 9C4.67893 9 3 10.6789 3 12.75C3 14.8211 4.67893 16.5 6.75 16.5C7.55251 16.5 8.31708 16.243 8.91664 15.7847L15.0627 19.9517C15.022 20.1281 15 20.3113 15 20.5C15 22.1569 16.3431 23.5 18 23.5C19.6569 23.5 21 22.1569 21 20.5C21 18.8431 19.6569 17.5 18 17.5C16.3431 17.5 15 18.8431 15 20.5C15 20.3113 15.022 20.1281 15.0627 19.9517L8.91664 15.7847C8.31708 16.243 7.55251 16.5 6.75 16.5C4.67893 16.5 3 14.8211 3 12.75C3 10.6789 4.67893 9 6.75 9C7.55251 9 8.31708 9.25696 8.91664 9.71529L15.0627 5.54826C15.022 5.37189 15 5.18871 15 5C15 3.34315 16.3431 2 18 2C19.6569 2 21 3.34315 21 5C21 6.65685 19.6569 8 18 8Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                </div>
                <div
                  v-if="classDetails[cls.id].stats"
                  class="chart-max-hr-label"
                >
                  <span class="max-hr-arrow">▲</span>
                  {{ classDetails[cls.id].stats.maxHr }}
                </div>
                <HrLineChart
                  :data="classDetails[cls.id].hr_trace"
                  :yMin="60"
                  :yMax="200"
                  :xMax="classDetails[cls.id].duration"
                  :zoneBoundaries="classDetails[cls.id].zone_boundaries"
                />
                <div class="chart-footer">
                  <div class="chart-min-hr-label">
                    <span class="min-hr-arrow">▼</span>
                    {{ getMinHr(classDetails[cls.id].hr_trace) }}
                  </div>
                  <div class="chart-duration-label">
                    {{ formatDuration(classDetails[cls.id].duration) }}
                  </div>
                </div>
              </div>

              <!-- 통계 그리드 -->
              <div v-if="classDetails[cls.id]" class="stats-grid">
                <div class="stat-column-left">
                  <div class="stat-card">
                    <div class="stat-label">소모 칼로리</div>
                    <div class="stat-value orange">
                      {{ classDetails[cls.id].stats.calories
                      }}<span class="unit">kcal</span>
                    </div>
                    <button
                      class="recalc-btn"
                      :disabled="recalcLoading"
                      @click="recalcCalories(cls.id, 'detail')"
                    >
                      {{ recalcLoading ? "계산 중..." : "칼로리 재계산" }}
                    </button>
                  </div>

                  <div class="stat-card">
                    <div class="stat-label">Zone 4+</div>
                    <div class="stat-value purple">
                      {{ Math.round(classDetails[cls.id].stats.zone4plus)
                      }}<span class="unit">분</span>
                    </div>
                  </div>
                </div>

                <div class="stat-card stat-card-hr">
                  <div class="stat-label">최대 심박수</div>
                  <!-- 데스크톱: 도넛 차트 -->
                  <DoughnutChart
                    v-if="!isMobile"
                    :maxHr="classDetails[cls.id].stats.maxHr"
                    :percentage="classDetails[cls.id].stats.maxHrPercent"
                  />
                  <!-- 모바일: 심플 텍스트 -->
                  <div v-else class="hr-mobile-display">
                    <div class="hr-number">
                      {{ classDetails[cls.id].stats.maxHr }}
                    </div>
                    <div class="hr-unit">BPM</div>
                  </div>
                </div>

                <div class="stat-card stat-card-zones">
                  <div class="zone-list">
                    <ZoneBar
                      v-for="z in sortedZones(classDetails[cls.id].stats.zones)"
                      :key="z.name"
                      :zone="z"
                    />
                  </div>
                </div>
              </div>
              <div class="logo-container">
                <svg
                  width="126"
                  height="16"
                  viewBox="0 0 126 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="logo-image"
                >
                  <path
                    d="M119.141 3.39808H120.065L122.452 7.24922H122.52L124.907 3.39808H125.83L122.891 8.05999V11.5508H122.08V8.05999L119.141 3.39808Z"
                    fill="white"
                  />
                  <path
                    d="M113.632 11.5508V3.39808H116.301C118.057 3.39808 118.947 4.4228 118.947 5.8529C118.947 6.94518 118.429 7.7841 117.415 8.11629L119.285 11.5508H118.361L116.593 8.27394L116.312 8.2852H114.431V11.5508H113.632ZM114.431 7.55326H116.278C117.55 7.55326 118.159 6.90014 118.159 5.8529C118.159 4.80566 117.55 4.11876 116.267 4.11876H114.431V7.55326Z"
                    fill="white"
                  />
                  <path
                    d="M112.39 7.47444C112.39 10.0306 110.915 11.6634 108.854 11.6634C106.782 11.6634 105.307 10.0306 105.307 7.47444C105.307 4.91827 106.782 3.28547 108.854 3.28547C110.915 3.28547 112.39 4.91827 112.39 7.47444ZM111.613 7.47444C111.613 5.30113 110.431 4.03994 108.854 4.03994C107.278 4.03994 106.095 5.30113 106.095 7.47444C106.095 9.63648 107.266 10.9089 108.854 10.9089C110.431 10.9089 111.613 9.64774 111.613 7.47444Z"
                    fill="white"
                  />
                  <path
                    d="M98.9416 4.11876V3.39808H104.898V4.11876H102.331V11.5508H101.52V4.11876H98.9416Z"
                    fill="white"
                  />
                  <path
                    d="M97.4408 5.95425C97.2156 4.83944 96.2471 4.03994 95.0085 4.03994C93.477 4.03994 92.2496 5.24483 92.2496 7.47444C92.2496 9.70405 93.477 10.9089 95.0085 10.9089C96.2471 10.9089 97.2156 10.1094 97.4408 8.99463H98.2515C97.9813 10.5148 96.7313 11.6634 95.0085 11.6634C92.9365 11.6634 91.4614 10.0306 91.4614 7.47444C91.4614 4.91827 92.9365 3.28547 95.0085 3.28547C96.7313 3.28547 97.97 4.41154 98.2515 5.95425H97.4408Z"
                    fill="white"
                  />
                  <path
                    d="M85.4225 11.5508H84.5779L87.5395 3.39808H88.4066L91.3681 11.5508H90.5236L89.6678 9.12975H86.2783L85.4225 11.5508ZM86.5373 8.42033H89.42L88.0012 4.43406H87.9449L86.5373 8.42033Z"
                    fill="white"
                  />
                  <path
                    d="M80.3183 11.5508V3.39808H85.0815V4.11876H81.1178V7.11409H84.7099V7.82352H81.1178V11.5508H80.3183Z"
                    fill="white"
                  />
                  <path
                    d="M79.2003 7.47444C79.2003 10.1883 77.4662 11.6634 75.2591 11.6634C73.0295 11.6634 71.3066 10.177 71.3066 7.47444C71.3066 4.76062 73.0295 3.28547 75.2591 3.28547C77.4662 3.28547 79.2003 4.76062 79.2003 7.47444ZM76.9932 7.47444C76.9932 5.97677 76.3739 5.15474 75.2591 5.15474C74.133 5.15474 73.5137 5.97677 73.5137 7.47444C73.5137 8.9721 74.133 9.79413 75.2591 9.79413C76.3739 9.79413 76.9932 8.9721 76.9932 7.47444Z"
                    fill="white"
                  />
                  <path
                    d="M70.6053 3.39808V11.5508H68.4657V3.39808H70.6053Z"
                    fill="white"
                  />
                  <path
                    d="M63.7106 11.5508H60.6139V3.39808H63.6881C66.1992 3.39808 67.7644 4.92953 67.7644 7.47444C67.7644 10.0193 66.1992 11.5508 63.7106 11.5508ZM62.7534 9.72657H63.6318C64.893 9.72657 65.6024 9.19732 65.6024 7.47444C65.6024 5.75156 64.893 5.2223 63.5755 5.2223H62.7534V9.72657Z"
                    fill="white"
                  />
                  <path
                    d="M53.5053 11.5508V3.39808H56.9736C58.7978 3.39808 59.9914 4.43406 59.9914 6.15694C59.9914 7.283 59.4734 8.08251 58.5951 8.49916L60.2617 11.5508H57.9307L56.4894 8.83698H55.6448V11.5508H53.5053ZM55.6448 7.15914H56.4781C57.2889 7.15914 57.7506 6.86636 57.7506 6.15694C57.7506 5.43626 57.2889 5.1097 56.4781 5.1097H55.6448V7.15914Z"
                    fill="white"
                  />
                  <path
                    d="M47.0273 11.5508H44.7076L47.4214 3.39808H50.3379L53.0405 11.5508H50.732L50.2478 9.95178H47.5115L47.0273 11.5508ZM48.0069 8.33025H49.7523L48.9078 5.59391H48.8402L48.0069 8.33025Z"
                    fill="white"
                  />
                  <path
                    d="M42.3847 6.4272C42.3172 5.66147 41.7992 5.15474 40.9321 5.15474C39.806 5.15474 39.1642 6.02181 39.1642 7.47444C39.1642 9.00589 39.8173 9.79413 40.9208 9.79413C41.7429 9.79413 42.2721 9.3437 42.3847 8.62302L44.558 8.64554C44.4454 10.0419 43.218 11.6634 40.8758 11.6634C38.6349 11.6634 36.9571 10.177 36.9571 7.47444C36.9571 4.76062 38.68 3.28547 40.8758 3.28547C42.9027 3.28547 44.3778 4.4228 44.558 6.4272H42.3847Z"
                    fill="white"
                  />
                  <path
                    d="M5.76545 0C2.58124 0 0 2.58128 0 5.76545V9.60909C0 12.7933 2.58124 15.3745 5.76545 15.3745H20.6403V7.57196L24.484 12.8377H30.7491V7.57196H20.6403V5.26578H30.7491V0H21.14C17.9558 0 15.3745 2.58128 15.3745 5.76545V10.1088H5.26578V5.26578H11.5309L15.3745 0H5.76545Z"
                    fill="white"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div v-if="loadingMore" class="loading-more">로딩 중...</div>

        <div v-if="!loading && classes.length === 0" class="end-message">
          이 주차에는 운동 기록이 없습니다.
        </div>
      </template>
    </div>

    <!-- 이관 모달 -->
    <div
      v-if="transferModal"
      class="modal-overlay"
      @click.self="closeTransferModal"
    >
      <div class="transfer-modal">
        <h3>운동기록 이관</h3>
        <p class="transfer-desc">
          <strong>{{ transferTarget?.guest_name }}</strong
          >의 운동기록을 실제 회원에게 이관합니다.
        </p>
        <div class="form-row">
          <input
            v-model="memberQuery"
            @input="searchTransferMembers"
            placeholder="이름 또는 전화번호로 검색"
            class="input"
          />
        </div>
        <div v-if="memberResults.length" class="member-results">
          <div
            v-for="m in memberResults"
            :key="m.id"
            class="member-row"
            :class="{ selected: selectedMember?.id === m.id }"
            @click="selectedMember = m"
          >
            {{ m.name }} ({{ m.phone_number }})
          </div>
        </div>
        <!-- 2026-10-24 사장님 결정 A: Hyrox·Running·기타 수업은 횟수권을 차감하지 않는다(서버도 deduct_membership를 무시하고
             회원권을 찾지 않는다). 기록에 저장된 수업 타입으로 본다(isCreditFreeClassType) -->
        <p v-if="transferCreditFree" class="deduct-row deduct-row--note">
          이 수업은 횟수권을 차감하지 않아요.
        </p>
        <label v-else class="deduct-row">
          <input type="checkbox" v-model="deductMembership" />
          횟수권 1회 차감 (무제한·주2회권, Hyrox·Running·기타는 차감 안 함)
        </label>
        <div class="modal-actions">
          <button class="btn-cancel" @click="closeTransferModal">취소</button>
          <button
            class="btn-transfer"
            :disabled="!selectedMember || transferring"
            @click="doTransfer"
          >
            {{ transferring ? "이관 중..." : "이관" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HrLineChart from "@/components/HrLineChart.vue";
import DoughnutChart from "@/components/DoughnutChart.vue";
import ZoneBar from "@/components/ZoneBar.vue";
import { apiJson } from "@/lib/api";
import { rpeDisplay } from "@/lib/classInfo";
import {
  classTypeIcon,
  classTypeTone,
  isCreditFreeClassType,
  itemTypeLabel,
} from "@/lib/classTypes";
import html2canvas from "html2canvas";

// api.js와 같은 API 주소 규칙(최근 기록 RPE를 조용히 읽을 때 쓴다)
const API_BASE =
  window.__APP_CONFIG__?.API_BASE || process.env.VUE_APP_API_BASE || "";

export default {
  name: "RecordsView",
  components: {
    HrLineChart,
    DoughnutChart,
    ZoneBar,
  },
  data() {
    return {
      loading: false,
      loadingMore: false,
      recentExpanded: true,
      recentClass: null,
      // R5: 최근 기록의 관리자 상세(GET /v1/admin/workouts/{id}). 기록 요약의 recentClass에는 rpe가 없어서 따로 읽는다
      recentDetail: null,
      classes: [],
      currentWeek: 0, // 0 = 이번주, 1 = 지난주, 2 = 2주전...
      maxWeeks: 12, // 최대 12주 표시
      showWeekPicker: false,
      isMobile: window.innerWidth <= 768,
      expandedClasses: {}, // 각 클래스의 expanded 상태
      loadingClassDetails: {}, // 각 클래스의 로딩 상태
      classDetails: {}, // 각 클래스의 상세 정보
      sharing: false, // 공유 중 상태
      recalcLoading: false,
      transferModal: false,
      transferTarget: null,
      memberQuery: "",
      memberResults: [],
      selectedMember: null,
      deductMembership: true,
      transferring: false,
    };
  },
  computed: {
    targetUserId() {
      return this.$route.params.user_id;
    },
    // R5 최근 기록 RPE: 같은 기록의 관리자 상세를 읽었으면 그 값, 아니면 요약 값(rpe 키가 없으면 안 그림)
    recentRpe() {
      const rc = this.recentClass;
      if (!rc) return null;
      const d = this.recentDetail;
      return rpeDisplay(d && d.id === rc.id ? d : rc);
    },
    // 이관할 기록의 수업이 횟수권을 차감하지 않는 타입인가(workouts.type = 수업 타입. 기록이 있는 수업은 타입을 못 바꾼다)
    transferCreditFree() {
      const t = this.transferTarget;
      return !!t && isCreditFreeClassType(t.type);
    },
  },
  async created() {
    // 쿼리 파라미터에서 week 값 확인
    const weekParam = this.$route.query.week;
    if (weekParam !== undefined) {
      const week = parseInt(weekParam);
      if (!isNaN(week) && week >= 0 && week < this.maxWeeks) {
        this.currentWeek = week;
      }
    }
    await this.loadRecords();
  },
  methods: {
    // R5 RPE 표시({ text: "RPE 적당", level } 또는 응답에 rpe가 없으면 null). lib/classInfo.js
    rpeOf(record) {
      return rpeDisplay(record);
    },
    async recalcCalories(workoutId, target) {
      if (!workoutId || this.recalcLoading) return;
      this.recalcLoading = true;
      try {
        const res = await apiJson(
          `/v1/admin/workouts/${workoutId}/recalc-calories`,
          { method: "POST" }
        );
        const newVal = res.calories || 0;
        if (target === "recent" && this.recentClass && this.recentClass.stats) {
          this.recentClass.stats.calories = newVal;
        } else if (
          this.classDetails &&
          this.classDetails[workoutId] &&
          this.classDetails[workoutId].stats
        ) {
          this.classDetails[workoutId].stats.calories = newVal;
        }
      } catch (e) {
        console.error("칼로리 재계산 실패:", e);
        alert(
          "칼로리 재계산 실패: 회원 프로필(성별/체중/생년월일)을 확인해주세요."
        );
      } finally {
        this.recalcLoading = false;
      }
    },
    openTransferModal(workout) {
      this.transferTarget = workout;
      this.transferModal = true;
      this.memberQuery = "";
      this.memberResults = [];
      this.selectedMember = null;
      this.deductMembership = true;
    },
    closeTransferModal() {
      this.transferModal = false;
      this.transferTarget = null;
    },
    async searchTransferMembers() {
      if (this.memberQuery.length < 2) {
        this.memberResults = [];
        return;
      }
      try {
        const data = await apiJson(
          `/v1/admin/members/search?q=${encodeURIComponent(this.memberQuery)}`
        );
        this.memberResults = (data.members || []).filter(
          (m) => !m.name.startsWith("무료체험")
        );
      } catch {
        this.memberResults = [];
      }
    },
    async doTransfer() {
      if (!this.selectedMember || !this.transferTarget) return;
      this.transferring = true;
      try {
        await apiJson(`/v1/admin/workouts/${this.transferTarget.id}/transfer`, {
          method: "POST",
          body: JSON.stringify({
            target_user_id: this.selectedMember.id,
            // 차감하지 않는 수업이면 늘 false(서버도 그 타입은 차감하지 않는다)
            deduct_membership:
              this.deductMembership && !this.transferCreditFree,
          }),
        });
        alert(`${this.selectedMember.name}에게 이관 완료`);
        this.closeTransferModal();
        await this.loadRecords();
      } catch (e) {
        alert("이관 실패: " + (e.message || "알 수 없는 오류"));
      }
      this.transferring = false;
    },
    sortedZones(zones) {
      if (!zones) return [];
      return [...zones].sort((a, b) => {
        const numA = parseInt(a.name.replace("Zone ", "").replace("Zone", ""));
        const numB = parseInt(b.name.replace("Zone ", "").replace("Zone", ""));
        return numB - numA;
      });
    },
    async loadRecords() {
      try {
        this.loading = true;

        // 현재 주차의 시작일과 끝일 계산
        const { startDate, endDate } = this.getWeekDateRange(this.currentWeek);

        // 관리자가 특정 회원의 기록을 조회
        const data = await apiJson(
          `/v1/admin/members/${this.targetUserId}/records/summary?start_date=${startDate}&end_date=${endDate}`
        );

        // week=0일 때만 recentClass 있음
        if (data.recentClass) {
          this.recentClass = data.recentClass;
        } else {
          this.recentClass = null;
        }

        this.classes = data.classes || [];
        this.loadRecentDetail();
      } catch (e) {
        console.error("Failed to load records:", e);
        this.recentClass = null;
        this.classes = [];
      } finally {
        this.loading = false;
      }
    },
    // R5: 최근 기록의 RPE를 관리자 상세에서 읽는다. 보조 정보라 실패해도 전역 오류 팝업 없이 넘어간다
    async loadRecentDetail() {
      const id = this.recentClass && this.recentClass.id;
      this.recentDetail = null;
      if (!id) return;
      try {
        const res = await fetch(
          `${API_BASE}/v1/admin/workouts/${encodeURIComponent(id)}`,
          { credentials: "include" }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (this.recentClass && this.recentClass.id === id) {
          this.recentDetail = data;
        }
      } catch {
        // RPE 표시만 빠진다
      }
    },
    goToPreviousWeek() {
      if (this.currentWeek < this.maxWeeks - 1) {
        this.currentWeek++;
        this.recentExpanded = true;
        this.loadRecords();
      }
    },
    goToNextWeek() {
      if (this.currentWeek > 0) {
        this.currentWeek--;
        this.recentExpanded = true;
        this.loadRecords();
      }
    },
    selectWeek(week) {
      this.currentWeek = week;
      this.showWeekPicker = false;
      this.recentExpanded = true;
      this.loadRecords();
    },
    getWeekDateRange(weekOffset) {
      const now = new Date();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay() - weekOffset * 7);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      // YYYY-MM-DD 형식으로 변환
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      return {
        startDate: formatDate(weekStart),
        endDate: formatDate(weekEnd),
      };
    },
    getWeekRangeText(weekOffset) {
      const now = new Date();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay() - weekOffset * 7);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const formatDate = (date) => {
        const m = date.getMonth() + 1;
        const d = date.getDate();
        return `${m}월 ${d}일`;
      };

      return `${formatDate(weekStart)} - ${formatDate(weekEnd)}`;
    },
    // R9 타입 색·아이콘·표시명(lib/classTypes.js). 새 9개 타입은 표시명(예: Pull & Cardio), 예전 3개는 값 그대로
    typeClass(type) {
      return classTypeTone(type);
    },
    getIcon(type) {
      try {
        return require(`@/assets/icons/${classTypeIcon(type)}.png`);
      } catch {
        return "";
      }
    },
    typeLabelOf(item) {
      return itemTypeLabel(item);
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 768;
    },
    formatTime(time) {
      if (!time) return "";
      // "11:00:00" -> "11:00" 형식으로 변환
      const timeStr = String(time);
      if (timeStr && timeStr.includes(":")) {
        const parts = timeStr.split(":");
        if (parts.length >= 2) {
          return `${parts[0]}:${parts[1]}`;
        }
      }
      return timeStr;
    },
    async toggleClassExpanded(classId) {
      // expanded 상태 토글
      this.expandedClasses[classId] = !this.expandedClasses[classId];

      // 펼쳤고, 아직 상세 정보를 로드하지 않았다면 로드
      if (
        this.expandedClasses[classId] &&
        !this.classDetails[classId] &&
        !this.loadingClassDetails[classId]
      ) {
        await this.loadClassDetails(classId);
      }
    },
    async loadClassDetails(classId) {
      try {
        this.loadingClassDetails[classId] = true;
        const data = await apiJson(`/v1/admin/workouts/${classId}`);
        this.classDetails[classId] = data;
      } catch (e) {
        console.error("Failed to load class details:", e);
      } finally {
        this.loadingClassDetails[classId] = false;
      }
    },
    getMinHr(hrTrace) {
      if (!hrTrace || hrTrace.length === 0) return 0;
      let minHr = hrTrace[0].hr_bpm;
      for (let i = 1; i < hrTrace.length; i++) {
        if (hrTrace[i].hr_bpm < minHr) {
          minHr = hrTrace[i].hr_bpm;
        }
      }
      return Math.round(minHr);
    },
    formatDuration(seconds) {
      if (!seconds || seconds === 0) return "0:00";
      const totalSeconds = Math.floor(seconds);
      const minutes = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      return `${minutes}:${secs.toString().padStart(2, "0")}`;
    },
    async shareWorkout(workoutId) {
      try {
        this.sharing = true;

        // workoutId가 'recent'면 recent-card 전체, 아니면 해당 클래스의 전체 카드 선택
        const selector =
          workoutId === "recent"
            ? ".recent-card"
            : `.class-item-wrapper[data-workout-id="${workoutId}"]`;

        // DOM 요소 찾기
        await this.$nextTick();
        const element = document.querySelector(selector);

        if (!element) {
          console.error("Workout element not found");
          return;
        }

        // html2canvas로 캡처 (모바일에서는 scale 조정)
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const canvas = await html2canvas(element, {
          backgroundColor: "#0a0a0a",
          scale: this.isMobile ? 1.5 : 2, // 모바일에서는 scale 낮춤 (메모리 최적화)
          useCORS: true,
          logging: false,
          allowTaint: false,
          removeContainer: true, // 메모리 최적화
        });

        // 모바일에서 이미지 처리
        if (this.isMobile) {
          // 모바일에서는 이미지를 새 창에서 열어서 저장/공유 가능하게 함
          const imageDataUrl = canvas.toDataURL("image/png", 0.95);

          // iOS 크롬 체크
          const isIOSChrome = isIOS && /CriOS/.test(navigator.userAgent);

          // iOS Safari나 files를 지원하는 경우 Web Share 시도
          if (!isIOSChrome && navigator.share && navigator.canShare) {
            try {
              canvas.toBlob(
                async (blob) => {
                  if (!blob) {
                    this.openImageInNewWindow(imageDataUrl);
                    this.sharing = false;
                    return;
                  }

                  const file = new File([blob], `workout-${Date.now()}.png`, {
                    type: "image/png",
                  });

                  // canShare로 files 지원 여부 확인
                  if (navigator.canShare({ files: [file] })) {
                    try {
                      await navigator.share({
                        title: "운동 기록",
                        files: [file],
                      });
                    } catch (err) {
                      if (err.name !== "AbortError") {
                        console.error("Share failed:", err);
                        this.openImageInNewWindow(imageDataUrl);
                      }
                    }
                  } else {
                    this.openImageInNewWindow(imageDataUrl);
                  }
                  this.sharing = false;
                },
                "image/png",
                0.95
              );
              return;
            } catch (err) {
              console.error("Web Share setup failed:", err);
              this.openImageInNewWindow(imageDataUrl);
              this.sharing = false;
              return;
            }
          } else {
            // iOS 크롬이나 files 미지원 시 이미지 새 창에서 열기
            this.openImageInNewWindow(imageDataUrl);
            this.sharing = false;
            return;
          }
        }

        // 데스크톱에서는 다운로드
        this.downloadImage(canvas);
        this.sharing = false;
      } catch (error) {
        console.error("Failed to share workout:", error);
        this.sharing = false;
      }
    },
    downloadImage(canvas) {
      // 다운로드 링크 생성
      const link = document.createElement("a");
      link.download = `workout-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    },
    openImageInNewWindow(imageDataUrl) {
      // 모바일에서 이미지를 새 창에서 열어서 저장/공유 가능하게 함
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>운동 기록</title>
              <style>
                body {
                  margin: 0;
                  padding: 20px;
                  background: #000;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  min-height: 100vh;
                }
                img {
                  max-width: 100%;
                  height: auto;
                  border-radius: 10px;
                }
                .instructions {
                  color: #fff;
                  text-align: center;
                  margin-top: 20px;
                  font-size: 14px;
                  padding: 0 20px;
                }
              </style>
            </head>
            <body>
              <img src="${imageDataUrl}" alt="운동 기록" />
              <div class="instructions">
                이미지를 길게 눌러 저장하거나 공유하세요
              </div>
            </body>
          </html>
        `);
        newWindow.document.close();
      } else {
        // 팝업이 차단된 경우 대체 방법
        alert(
          "팝업이 차단되었습니다. 이미지를 저장하려면 팝업을 허용해주세요."
        );
      }
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
.records-content {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  max-width: 100%;
}

.page-title {
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 400;
}

.main-container {
  background: #151718;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 400px;
}

/* 로딩 및 빈 상태 */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #8e8e8e;
}

.empty-state-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state p {
  margin: 8px 0;
  font-size: 18px;
}

.empty-state .hint {
  font-size: 14px;
  color: #666;
}

/* 최근 수업 카드 */
.recent-card {
  background: #0a0a0a;
  border-radius: 10px;
  padding: 24px;
  min-height: 118px;
}

.recent-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.recent-icon {
  width: 70px;
  height: 70px;
  border-radius: 5px;
  border: 1px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.recent-icon.hyb {
  border-color: #1f472d;
  background: rgba(31, 71, 45, 0.2);
}

.recent-icon.core {
  border-color: #4e3511;
  background: rgba(78, 53, 17, 0.2);
}

.recent-icon.car {
  border-color: #4d1c1b;
  background: rgba(77, 28, 27, 0.3);
}

.icon {
  width: 32px;
  height: 32px;
}

.recent-info {
  flex: 1;
}

.recent-info h3 {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 400;
}

.recent-meta {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
}

.recent-meta .time {
  color: #8e8e8e;
  font-size: 18px;
  white-space: nowrap;
}

.recent-meta .type {
  font-size: 18px;
  white-space: nowrap;
}

.recent-meta .type.hyb {
  color: #49de80;
}
.recent-meta .type.core {
  color: #f59e0c;
}
.recent-meta .type.car {
  color: #ef4444;
}

/* R5 RPE(회원이 고른 체감 강도): 1 여유 · 2 적당 · 3 힘듦 · 0 미입력 */
.rpe-tag {
  font-size: 13px;
  padding: 2px 8px;
  border-radius: 5px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #c8c8c8;
  white-space: nowrap;
}
.rpe-tag--0 {
  color: #8e8e8e;
  border-style: dashed;
}
.rpe-tag--1 {
  color: #49de80;
  border-color: rgba(73, 222, 128, 0.4);
}
.rpe-tag--2 {
  color: #f59e0c;
  border-color: rgba(245, 158, 12, 0.4);
}
.rpe-tag--3 {
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.4);
}

.recent-badge-inline {
  padding: 4px 10px;
  background: rgba(31, 71, 45, 0.3);
  border: 1px solid #49de80;
  border-radius: 5px;
  color: #49de80;
  font-size: 14px;
  font-weight: 400;
  margin-left: 8px;
}

.expand-btn-main {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  align-self: flex-start;
}

.recent-expanded {
  margin-top: 4px;
  padding-top: 4px;
}

.hr-chart-container {
  background: #0a0a0a;
  border: 1px solid rgba(114, 114, 114, 0.2);
  border-radius: 5px;
  padding: 16px;
  margin-bottom: 24px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 0;
}

.chart-title {
  color: #fff;
  font-size: 16px;
}

.chart-max-hr-label {
  color: #fff;
  font-size: 14px;
  margin-bottom: 4px;
  margin-left: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.max-hr-arrow {
  font-size: 8px;
  line-height: 1;
  opacity: 0.8;
}

.chart-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0;
}

.chart-min-hr-label {
  color: #fff;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.min-hr-arrow {
  font-size: 8px;
  line-height: 1;
  opacity: 0.8;
}

.chart-duration-label {
  color: #fff;
  font-size: 14px;
}

.chart-subtitle {
  color: #8e8e8e;
  font-size: 14px;
}

/* 통계 그리드 */
.stats-grid {
  display: grid;
  grid-template-columns: 166px 166px 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 12px;
  margin-bottom: 0;
}

.stat-column-left {
  display: flex;
  flex-direction: column;
  gap: 12px;
  grid-row: 1 / 3;
}

.stat-card {
  background: #0a0a0a;
  border: 1px solid rgba(114, 114, 114, 0.2);
  border-radius: 5px;
  padding: 16px;
  flex: 1;
}

.stat-card-hr {
  grid-column: 2;
  grid-row: 1 / 3;
}

.stat-card-zones {
  grid-column: 3;
  grid-row: 1 / 3;
}

.stat-label {
  color: #fff;
  font-size: 18px;
  margin-bottom: 12px;
}

.stat-value {
  font-size: 30px;
  font-weight: 400;
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.stat-value.orange {
  color: #f59e0c;
}
.stat-value.purple {
  color: #8b5cf6;
}

.stat-value .unit {
  font-size: 16px;
  color: #8e8e8e;
  margin-bottom: 4px;
}

.zone-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.share-button-icon {
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.share-button-icon:hover:not(:disabled) {
  opacity: 1;
}

.share-button-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.share-button-icon svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  width: 100%;
}

.logo-image {
  width: 260px;
  height: auto;
  display: block;
}

/* 게스트 태그 */
.guest-tag {
  background: #ff817f;
  color: #fff;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 4px;
}
.transfer-btn {
  background: #1b1d1e;
  border: 1px solid #78ff47;
  color: #78ff47;
  font-size: 11px;
  padding: 2px 10px;
  border-radius: 4px;
  margin-left: 6px;
  cursor: pointer;
}

/* 이관 모달 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.transfer-modal {
  background: #1b1d1e;
  border: 1px solid #3a3c3d;
  border-radius: 10px;
  padding: 24px;
  width: 400px;
  max-width: 90%;
  color: #fff;
}
.transfer-modal h3 {
  font-size: 18px;
  margin-bottom: 12px;
}
.transfer-desc {
  font-size: 14px;
  color: #b2b2b2;
  margin-bottom: 16px;
}
.transfer-modal .input {
  width: 100%;
  padding: 10px;
  background: #0a0a0a;
  border: 1px solid #333;
  color: #fff;
  border-radius: 6px;
  margin-bottom: 12px;
}
.member-results {
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 12px;
  border: 1px solid #333;
  border-radius: 6px;
}
.member-row {
  padding: 10px;
  cursor: pointer;
  font-size: 14px;
  border-bottom: 1px solid #222;
}
.member-row:hover {
  background: #2a2a2a;
}
.member-row.selected {
  background: #3a3a3a;
  color: #78ff47;
}
.deduct-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #b2b2b2;
  margin-bottom: 16px;
}
.deduct-row--note {
  margin-top: 0;
  color: #f59e0c;
}
.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.btn-cancel,
.btn-transfer {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}
.btn-cancel {
  background: #333;
  color: #fff;
}
.btn-transfer {
  background: #78ff47;
  color: #000;
  font-weight: 600;
}
.btn-transfer:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 수업 리스트 */
.class-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.class-item-wrapper {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

/* 주차 선택 UI */
.week-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  max-width: 700px;
}

.week-nav-btn {
  box-sizing: border-box;
  width: 54px;
  height: 54px;
  background: rgba(51, 51, 51, 0.3);
  border: 1px solid #333333;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  flex-shrink: 0;
}

.week-nav-btn:hover:not(:disabled) {
  background: rgba(51, 51, 51, 0.5);
}

.week-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.week-selector-main {
  flex: 1;
  height: 54px;
  background: #1b1d1e;
  border: 1px solid #333333;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  transition: background 0.2s;
  position: relative;
}

.week-selector-main:hover {
  background: #242627;
}

.week-text {
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  color: #ffffff;
  letter-spacing: -0.02em;
}

.current-badge {
  padding: 2px 8px;
  background: #2f4b28;
  border-radius: 5px;
  font-family: "Pretendard", sans-serif;
  font-size: 14px;
  color: #78ff47;
  letter-spacing: -0.02em;
}

@media (max-width: 768px) {
  .current-badge {
    padding: 2px 4px;
    font-size: 12px;
  }
}

.dropdown-icon {
  margin-left: auto;
}

/* 주차 선택 드롭다운 */
.week-picker-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 66px;
  width: 450px;
  max-height: 500px;
  background: #1b1d1e;
  border-radius: 5px;
  padding: 12px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  scrollbar-width: thin;
  scrollbar-color: #333 transparent;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.week-picker-dropdown::-webkit-scrollbar {
  width: 6px;
}

.week-picker-dropdown::-webkit-scrollbar-track {
  background: transparent;
}

.week-picker-dropdown::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 3px;
}

.week-picker-item {
  width: 100%;
  min-height: 56px;
  background: #0a0a0a;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 16px;
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  color: #ffffff;
  letter-spacing: -0.02em;
  transition: background 0.2s;
  text-align: left;
  white-space: nowrap;
}

.week-picker-item span:first-child {
  flex: 0 0 auto;
}

.week-picker-item:hover {
  background: #1a1a1a;
}

.week-picker-item.active {
  background: #1a1a1a;
  color: #78ff47;
}

.current-badge-small {
  padding: 2px 6px;
  background: #2f4b28;
  border-radius: 3px;
  font-size: 12px;
  color: #78ff47;
}

.loading-more {
  text-align: center;
  color: #8e8e8e;
  font-size: 14px;
  padding: 16px;
}

.end-message {
  text-align: center;
  color: #8e8e8e;
  font-size: 14px;
  padding: 16px;
}

@media (max-width: 1400px) {
  .stats-grid {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
  }

  .stat-column-left {
    grid-column: 1;
  }

  .stat-card-hr {
    grid-column: 2;
    grid-row: 1;
  }

  .stat-card-zones {
    grid-column: 1 / 3;
    grid-row: 2;
  }
}

@media (max-width: 1100px) {
  .grid {
    grid-template-columns: 1fr;
  }
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stat-card-hr,
  .stat-card-zones {
    grid-column: 1;
    grid-row: auto;
  }
}

/* 모바일 반응형 */
@media (max-width: 768px) {
  .main-container {
    padding: 16px;
    box-sizing: border-box;
    max-width: 100%;
    overflow-x: hidden;
  }

  /* 모든 자식 요소들도 box-sizing 적용 */
  .recent-card,
  .week-selector,
  .hr-chart-container,
  .stats-grid,
  .stat-card {
    box-sizing: border-box;
    max-width: 100%;
  }

  /* 최근 카드 가로폭 고정 */
  .recent-card {
    width: 100%;
  }

  .recent-expanded {
    width: 100%;
  }

  /* 차트 컨테이너 모바일 최적화 */
  .hr-chart-container {
    padding: 12px;
    margin-bottom: 0;
    overflow: hidden;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(114, 114, 114, 0.2);
    border-radius: 0;
    padding-bottom: 16px;
    margin-bottom: 16px;
  }

  /* 통계 그리드를 모바일에서 그리드로 변경 */
  .stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-auto-flow: dense;
    gap: 12px;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }

  /* stat-column-left의 두 카드도 그리드에 포함되도록 */
  .stat-column-left {
    display: contents;
  }

  /* Zone별 시간은 전체 너비로 첫 줄에 배치 */
  .stat-card-zones {
    grid-column: 1 / -1;
    grid-row: 1;
    background: transparent;
    border: none;
    padding: 0;
    padding-bottom: 0;
    margin-bottom: 16px;
    border-radius: 0;
  }

  /* zone-list 아래에 선 추가 */
  .stat-card-zones .zone-list {
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(114, 114, 114, 0.2);
    margin-bottom: 0;
  }

  /* 나머지 카드들은 같은 높이로 */
  .stat-card {
    min-height: auto;
    display: flex;
    flex-direction: column;
    height: 100%;
    background: transparent;
    border: none;
    padding: 0;
  }

  /* 모든 stat-label의 높이를 compact하게 */
  .stat-card .stat-label {
    margin-bottom: 8px;
    font-size: 12px;
  }

  /* 칼로리, zone4+는 두 번째 행에 배치 */
  .stat-column-left > .stat-card {
    grid-row: 2;
  }

  /* 칼로리와 Zone 4+ 사이에 세로선 추가 */
  .stat-column-left > .stat-card:first-child {
    border-right: 1px solid rgba(114, 114, 114, 0.2);
    border-radius: 0;
    padding-right: 12px;
  }

  .stat-column-left > .stat-card:last-child {
    padding-left: 12px;
  }

  /* 최대 심박수는 두 번째 행의 세 번째 열에 배치 */
  .stat-card-hr {
    grid-row: 2;
    grid-column: 3;
    border-left: 1px solid rgba(114, 114, 114, 0.2);
    border-radius: 0;
    padding-left: 12px;
  }

  /* 모든 카드 내용 가운데 정렬 (Zone별 시간 제외) */
  .stat-card:not(.stat-card-zones) {
    align-items: center;
    text-align: center;
    justify-content: center;
  }

  .stat-value {
    justify-content: center;
    align-items: center;
    font-size: 24px;
  }

  .stat-value .unit {
    margin-bottom: 0;
    font-size: 10px;
  }

  /* 최대 심박수 모바일 표시 */
  .hr-mobile-display {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    gap: 6px;
    padding: 8px 0;
  }

  .hr-number {
    color: #ef4444;
    font-size: 24px;
    font-weight: 400;
    line-height: 1;
  }

  .hr-unit {
    color: #8e8e8e;
    font-size: 10px;
  }

  /* 주차 선택 UI 모바일 최적화 */
  .week-selector {
    max-width: 100%;
    flex-wrap: wrap;
  }

  .week-picker-dropdown {
    left: 0;
    right: 0;
    width: auto;
  }

  /* 최근 카드 패딩 조정 */
  .recent-card {
    padding: 16px;
  }

  .class-item-wrapper {
    padding: 16px;
    width: 100%;
    overflow-x: hidden;
  }

  /* 페이지 타이틀 */
  .page-title {
    font-size: 20px;
    margin-bottom: 16px;
  }

  /* 최근 카드 헤더 모바일 최적화 */
  .recent-header {
    flex-wrap: wrap;
    gap: 12px;
  }

  .recent-icon {
    width: 60px;
    height: 60px;
  }

  .recent-info h3 {
    font-size: 20px;
  }

  .recent-meta {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex-wrap: nowrap;
  }

  .recent-meta .time,
  .recent-meta .type {
    font-size: 16px;
    white-space: nowrap;
  }
}

.recalc-btn {
  margin-top: 8px;
  padding: 6px 12px;
  background: #ff7a00;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}
.recalc-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
